import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { unparse } from "papaparse";
import { Box } from "@mui/material";
import FeatherIcon from "feather-icons-react";
import type { QuestionTextTranslations } from "@green0meter-back/doubleMateriality/dtos/types/QuestionAnswerRow.type";
import { DoubleMaterialityQuestionTimeHorizon } from "@green0meter-back/doubleMateriality/enums/DoubleMaterialityQuestion.enums";
import { DoubleMaterialityIroTypeEnum } from "@green0meter/api-client";

import { getQuestionIro } from "./getQuestionIro";

import { Button } from "@components/base";
import { QuestionAnswerRow } from "@api/hooks/doubleMateriality/GetQuestionAnswers/useGetQuestionAnswers";
import { useUserStore } from "@store/user";
import { useGetAssessments } from "@api";

interface DMStakeholder {
  id: string;
  fullName: string;
  email: string;
  companyName: string;
  type: string;
  deadline: string | null;
}

interface ExportButtonProps {
  stakeholders: DMStakeholder[];
  answers: QuestionAnswerRow[];
  year: number;
}
/**
 * exports stakeholders and their individual answers to a CSV file
 */
export const DMAnswersExportButton: React.FC<ExportButtonProps> = ({
  stakeholders,
  answers,
  year,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUserStore();

  const { assessments, isPendingGetAssessments } = useGetAssessments(user, {
    year,
    pageNumber: 1,
    pageSize: 1000,
  });

  const handleExport = (): void => {
    if (!stakeholders.length) {
      enqueueSnackbar(t("doubleMateriality.export.noData", "No data to export"), {
        variant: "warning",
      });

      return;
    }

    if (isPendingGetAssessments) {
      enqueueSnackbar(t("doubleMateriality.export.loadingData", "Loading data..."), {
        variant: "info",
      });

      return;
    }

    // Create CSV data with stakeholder info and their answers
    const csvData: Record<string, string | null | number>[] = [];

    stakeholders.forEach((stakeholder) => {
      const stakeholderAnswers = answers.filter(
        (answer) => answer.stakeholderId === stakeholder.id,
      );

      if (stakeholderAnswers.length === 0) {
        // Add stakeholder row with no answers
        csvData.push({
          "Stakeholder Full Name": stakeholder.fullName,
          "Stakeholder Email": stakeholder.email,
          "Stakeholder Company": stakeholder.companyName,
          "Stakeholder Type": stakeholder.type,
          Deadline: stakeholder.deadline
            ? new Date(stakeholder.deadline).toLocaleDateString()
            : "",
          "Sustainability Matter": "",
          IRO: "",
          Question: "",
          External: "",
          "Qualitative Answer": "",
          "Real/Potential": "",
          "Negative/Positive": "",
          "Level of Severity (1-5)": "",
          "Intensity (1-5)": "",
          "Range (1-5)": "",
          "Remediability (1-5)": "",
          "Probability (-3 to 3)": "",
          "Answered At": "",
          "Risk time horizon": "",
          "Impact time horizon": "",
          "Opportunity time horizon": "",
        });
      } else {
        // Add a row for each answer
        stakeholderAnswers.forEach((answer) => {
          const questionValue = (() => {
            const questions = answer.question?.questions;

            if (!questions) return "";

            if (typeof questions === "string") {
              return questions;
            }

            const translations = questions as QuestionTextTranslations;

            return (
              translations.en ??
              Object.values(translations).find((value) => value) ??
              ""
            );
          })();

          const sustainabilityMatter =
            answer.question?.assessment?.sustainabilityMatter ||
            answer.question?.sustainabilityMatter ||
            "";

          // Find assessment to determine IRO
          const assessment = assessments?.data?.find(
            (a) => a.sustainabilityMatter === sustainabilityMatter,
          );
          const iroParts = [];

          if (assessment?.impacts) iroParts.push("Impact");
          if (assessment?.risks) iroParts.push("Risk");
          if (assessment?.opportunities) iroParts.push("Opportunity");
          const iro = iroParts.join(", ");

          const isPositive = answer.isPositiveImpact;
          const negativePositive =
            isPositive === null ? "" : isPositive ? "Positive" : "Negative";

          const timeHorizonStr =
            answer.timeHorizon
              ?.map(
                (h: DoubleMaterialityQuestionTimeHorizon) =>
                  DoubleMaterialityQuestionTimeHorizon[h],
              )
              .join(", ") ?? "";

          const iroType = getQuestionIro(answer.question);

          csvData.push({
            "Stakeholder Full Name": stakeholder.fullName,
            "Stakeholder Email": stakeholder.email,
            "Stakeholder Company": stakeholder.companyName,
            "Stakeholder Type": stakeholder.type,
            Deadline: stakeholder.deadline
              ? new Date(stakeholder.deadline).toLocaleDateString()
              : "",
            "Sustainability Matter": sustainabilityMatter,
            IRO: iro,
            Question: questionValue,
            External: answer.externalAnswer ?? "",
            "Qualitative Answer": answer.textAnswer || "",
            "Real/Potential":
              answer.isReal === null ? "" : answer.isReal ? "Real" : "Potential",
            "Negative/Positive": negativePositive,
            "Level of Severity (1-5)": answer.levelOfSeverity ?? "",
            "Intensity (1-5)": answer.intensity ?? "",
            "Range (1-5)": answer.range ?? "",
            "Remediability (1-5)": answer.remediability ?? "",
            "Probability (-3 to 3)": answer.probability ?? "",
            "Answered At": answer.answeredAt
              ? new Date(answer.answeredAt).toLocaleString()
              : "",
            "Risk time horizon":
              iroType === DoubleMaterialityIroTypeEnum.Risk ? timeHorizonStr : "",
            "Impact time horizon":
              iroType === DoubleMaterialityIroTypeEnum.Impact ? timeHorizonStr : "",
            "Opportunity time horizon":
              iroType === DoubleMaterialityIroTypeEnum.Opportunity
                ? timeHorizonStr
                : "",
          });
        });
      }
    });

    const csv = unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `double-materiality-results-${year}-${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      enqueueSnackbar(t("doubleMateriality.export.success", "Export successful"), {
        variant: "success",
      });
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
      <Button
        variant="text"
        size="small"
        startIcon={<FeatherIcon icon="download" size={8} />}
        onClick={handleExport}
        sx={{
          textTransform: "none",
          px: 2,
        }}
      >
        {t("doubleMateriality.exportCSV", "Export")}
      </Button>
    </Box>
  );
};
